import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

import { resetPassword, clearAuthStatus } from '@/redux/slices/auth.slice';

import { NS } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/redux';
import { CallToActionKind } from '@/types';
import { getApiErrorMessage } from '@/utils/millesimaUtils';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import FormValidator from '@/components/common/form/FormValidator';
import TextValidator from '@/components/common/form/TextValidator';

import styles from './ForgotPassword.module.scss';

const AlertMessage = dynamic(() => import('@/components/common/AlertMessage'), { ssr: false });
AlertMessage.displayName = 'AlertMessage';

export const ForgotPassword = ({ handleCancel }) => {
  const resetStatus = useAppSelector(({ auth }) => auth.status.reset);
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string, error: boolean }>();
  const [email, setEmail] = useState<string>('');
  const { t } = useTranslation(NS.COMMON);

  useEffect(() => {
    dispatch(clearAuthStatus({ name: 'reset' }));
  }, [clearAuthStatus]);

  useEffect(() => {
    if (submitted && resetStatus) {
      if (resetStatus.success) {
        setMessage({ error: false, text: t('forms.mailSent') });
      } else if (resetStatus.error) {
        setMessage({ error: true, text: getApiErrorMessage(t, resetStatus.error) });
      }
    }
  }, [resetStatus, submitted, t]);

  const handleSubmit = () => {
    dispatch(resetPassword({ email }));
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    setMessage(undefined);
  };

  return (
    <div data-test-id="forgot-password" data-rbf="login-form-forgot-password">
      <FormValidator onSubmit={handleSubmit}>
        <TextValidator
          id="email-forgot-password"
          onChange={handleChange}
          name="email"
          type="email"
          label={t('forms.email')}
          value={email}
          validators={['required', 'isEmail']}
          errorMessages={[t('forms.requiredField'), t('forms.validEmail')]}
          data-test-id="email-forgot-password"
        />
        {message && (
          <div className={styles.message} data-test-id="forgot-password-message">
            <AlertMessage error={message.error} message={message.text} />
          </div>
        )}
        <div className={styles['submit-container']}>
          {message ? (
            <CallToActionContainer
              id="forgot-password-cancel"
              kind={CallToActionKind.SECONDARY}
              type="button"
              onClick={handleCancel}
            >
              {t('btn.continue')}
            </CallToActionContainer>
          ) : (
            <>
              <CallToActionContainer
                id="forgot-password-cancel"
                kind={CallToActionKind.SECONDARY}
                type="button"
                onClick={handleCancel}
              >
                {t('btn.cancel')}
              </CallToActionContainer>
              <CallToActionContainer id="forgot-password-submit" type="submit">{t('btn.validate')}</CallToActionContainer>
            </>
          )}
        </div>
      </FormValidator>
    </div>
  );
};

export default ForgotPassword;
